import { useState } from 'react'
import { useBasket } from '../state/BasketContext'
import { useTranslation } from '../i18n/LanguageContext'
import { sortFoodsByCategoryAndName } from '../scoring'
import { getPortionPrice } from '../types'
import { translateFoodState, foodStateBadgeClass, translateNutrient } from '../i18n/nutrientTranslations'
import { translateFoodName, translateFoodCategory } from '../i18n/foodTranslations'

export function SuggestionsView() {
  const { scoreResult, addFood, removeFood, nutrients, customPortions, updateCustomPortion, resetCustomPortion, customPurchaseInfo, updateCustomPurchaseInfo, resetCustomPurchaseInfo } = useBasket()
  const { t, lang } = useTranslation()
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'nutrientPer100g' | 'nutrientPerPortion'>('category')
  const [selectedNutrientId, setSelectedNutrientId] = useState<number | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [hideFodmap, setHideFodmap] = useState(false)

  const suggestedFoods = sortFoodsByCategoryAndName(scoreResult.foodOptions)

  const categories = ['all', ...new Set(suggestedFoods.map(option => option.food.category))]

  let filteredFoods = suggestedFoods.filter(option => {
    const localizedName = translateFoodName(option.food.name, lang)
    const localizedCategory = translateFoodCategory(option.food.category, lang)
    const localizedState = translateFoodState(option.food.state, lang)
    const q = searchQuery.toLowerCase()
    return (
      (filterCategory === 'all' || option.food.category === filterCategory) &&
      (searchQuery === '' ||
        option.food.name.toLowerCase().includes(q) ||
        localizedName.toLowerCase().includes(q) ||
        option.food.category.toLowerCase().includes(q) ||
        localizedCategory.toLowerCase().includes(q) ||
        option.food.state.toLowerCase().includes(q) ||
        localizedState.toLowerCase().includes(q)) &&
      (!hideFodmap || !option.food.highFodmap)
    )
  })

  const seenFoodIds = new Set<string>()
  filteredFoods = filteredFoods.filter(option => {
    if (seenFoodIds.has(option.food.id)) return false
    seenFoodIds.add(option.food.id)
    return true
  })

  if (sortBy === 'name') {
    filteredFoods = filteredFoods.sort((a, b) =>
      translateFoodName(a.food.name, lang).localeCompare(translateFoodName(b.food.name, lang))
    )
  } else if (sortBy === 'category') {
    filteredFoods = filteredFoods.sort((a, b) => {
      const catA = translateFoodCategory(a.food.category, lang)
      const catB = translateFoodCategory(b.food.category, lang)
      const catDiff = catA.localeCompare(catB)
      if (catDiff !== 0) return catDiff
      return translateFoodName(a.food.name, lang).localeCompare(translateFoodName(b.food.name, lang))
    })
  } else if ((sortBy === 'nutrientPer100g' || sortBy === 'nutrientPerPortion') && selectedNutrientId) {
    filteredFoods = filteredFoods.sort((a, b) => {
      if (sortBy === 'nutrientPer100g') {
        const aValue = a.food.nutrients[selectedNutrientId] || 0
        const bValue = b.food.nutrients[selectedNutrientId] || 0
        return bValue - aValue
      } else {
        const aValuePer100g = a.food.nutrients[selectedNutrientId] || 0
        const bValuePer100g = b.food.nutrients[selectedNutrientId] || 0
        const aValuePerPortion = aValuePer100g * (a.food.portionGrams / 100)
        const bValuePerPortion = bValuePer100g * (b.food.portionGrams / 100)
        return bValuePerPortion - aValuePerPortion
      }
    })
  }

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded-2 border">
        <h4 className="h5 mb-0">
          <i className="bi bi-lightbulb me-2"></i>
          {t('suggestions.title')}
        </h4>
        <div className="d-flex align-items-center gap-2 text-muted small">
          <span>{t('suggestions.available', { count: filteredFoods.length })}</span>
          {(Object.keys(customPortions).length > 0 || Object.keys(customPurchaseInfo).length > 0) && (
            <>
              <span className="text-muted">|</span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => {
                  Object.keys(customPortions).forEach(foodId => resetCustomPortion(foodId))
                  Object.keys(customPurchaseInfo).forEach(foodId => resetCustomPurchaseInfo(foodId))
                }}
                title={t('suggestions.resetCustomTooltip')}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>{t('suggestions.resetCustomLabel')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-2">
        <div className="input-group input-group-sm">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={t('suggestions.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setSearchQuery('')}
              title={t('suggestions.clearSearch')}
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
      </div>

      <div className="mb-2">
        <div className="d-flex gap-2">
          <div className="flex-fill">
            <select
              className="form-select form-select-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            >
              <option value="name">{t('suggestions.sort.name')}</option>
              <option value="category">{t('suggestions.sort.category')}</option>
              <option value="nutrientPer100g">{t('suggestions.sort.nutrientPer100g')}</option>
              <option value="nutrientPerPortion">{t('suggestions.sort.nutrientPerPortion')}</option>
            </select>
          </div>
          <div className="flex-fill">
            <select
              className="form-select form-select-sm"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? t('suggestions.filter.all') : translateFoodCategory(category, lang)}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex align-items-center">
            <div className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="hideFodmap"
                checked={hideFodmap}
                onChange={(e) => setHideFodmap(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="hideFodmap">
                {t('suggestions.hideFodmap')}
              </label>
            </div>
          </div>
        </div>

        {(sortBy === 'nutrientPer100g' || sortBy === 'nutrientPerPortion') && (
          <div className="mt-2">
            <div>
              <select
                className="form-select form-select-sm"
                value={selectedNutrientId || ''}
                onChange={(e) => setSelectedNutrientId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">{t('suggestions.selectNutrient')}</option>
                {nutrients.map(nutrient => (
                  <option key={nutrient.id} value={nutrient.id}>
                    {translateNutrient(nutrient.name, lang)} ({nutrient.unit})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="flex-grow-1 overflow-auto">
        {filteredFoods.length === 0 ? (
          <div className="text-center text-muted py-4">
            <p>{t('suggestions.noResults')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 kajchi-table-nowrap">
              <thead className="table-light sticky-top">
                <tr>
                  <th>{t('suggestions.column.food')}</th>
                  <th>{t('suggestions.column.category')}</th>
                  <th>{t('suggestions.column.state')}</th>
                  <th>{t('suggestions.column.packageSize')}</th>
                  <th>{t('suggestions.column.price')}</th>
                  <th>{t('suggestions.column.portion')}</th>
                  <th>{t('suggestions.column.portionPrice')}</th>
                  {(sortBy === 'nutrientPer100g' || sortBy === 'nutrientPerPortion') && selectedNutrientId && (
                    <th>{t('suggestions.column.content')}</th>
                  )}
                  <th className="text-center">{t('suggestions.column.basket')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFoods.map((option) => {
                  const { food } = option
                  const portionPrice = getPortionPrice(food)
                  const localizedName = translateFoodName(food.name, lang)

                  return (
                    <tr key={food.id}>
                      <td>
                        <span className="fw-medium">{localizedName}</span>
                      </td>

                      <td>
                        <small className="text-muted">{translateFoodCategory(food.category, lang)}</small>
                      </td>

                      <td>
                        <span className={`badge ${foodStateBadgeClass(food.state)}`}>{translateFoodState(food.state, lang)}</span>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="number"
                            className={`form-control form-control-sm ${customPurchaseInfo[food.id] ? 'border-primary' : ''}`}
                            value={Math.round(food.purchaseRawGrams)}
                            onChange={(e) => {
                              const newPurchaseRawGrams = parseInt(e.target.value)
                              if (!isNaN(newPurchaseRawGrams) && newPurchaseRawGrams > 0) {
                                updateCustomPurchaseInfo(food.id, {
                                  purchaseRawGrams: newPurchaseRawGrams,
                                  purchasePrice: food.purchasePrice
                                })
                              }
                            }}
                            style={{ width: '60px', fontSize: '11px', padding: '2px 4px' }}
                            step="1"
                            min="1"
                            title={t('suggestions.tooltip.packageSize')}
                          />
                          <small className="text-muted">g</small>
                          {customPurchaseInfo[food.id] && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => resetCustomPurchaseInfo(food.id)}
                              title={t('suggestions.tooltip.resetDefault')}
                              style={{ fontSize: '10px', padding: '1px 4px' }}
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="number"
                            className={`form-control form-control-sm ${customPurchaseInfo[food.id] ? 'border-primary' : ''}`}
                            value={Math.round(food.purchasePrice)}
                            onChange={(e) => {
                              const newPurchasePrice = parseInt(e.target.value)
                              if (!isNaN(newPurchasePrice) && newPurchasePrice >= 0) {
                                updateCustomPurchaseInfo(food.id, {
                                  purchaseRawGrams: food.purchaseRawGrams,
                                  purchasePrice: newPurchasePrice
                                })
                              }
                            }}
                            style={{ width: '60px', fontSize: '11px', padding: '2px 4px' }}
                            step="1"
                            min="0"
                            title={t('suggestions.tooltip.priceForint')}
                          />
                          <small className="text-muted">Ft</small>
                          {customPurchaseInfo[food.id] && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => resetCustomPurchaseInfo(food.id)}
                              title={t('suggestions.tooltip.resetDefault')}
                              style={{ fontSize: '10px', padding: '1px 4px' }}
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="number"
                            className={`form-control form-control-sm ${customPortions[food.id] ? 'border-primary' : ''}`}
                            value={Math.round(food.portionGrams)}
                            onChange={(e) => {
                              const newPortionGrams = parseInt(e.target.value)
                              if (!isNaN(newPortionGrams) && newPortionGrams > 0) {
                                updateCustomPortion(food.id, newPortionGrams)
                              }
                            }}
                            style={{ width: '60px', fontSize: '11px', padding: '2px 4px' }}
                            step="1"
                            min="1"
                            title={t('suggestions.tooltip.portionSize')}
                          />
                          <small className="text-muted">g</small>
                          {customPortions[food.id] && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => resetCustomPortion(food.id)}
                              title={t('suggestions.tooltip.resetDefault')}
                              style={{ fontSize: '10px', padding: '1px 4px' }}
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      </td>

                      <td>
                        <strong>{portionPrice.toFixed(0)}</strong>
                        <small className="text-muted ms-1">Ft</small>
                      </td>

                      {(sortBy === 'nutrientPer100g' || sortBy === 'nutrientPerPortion') && selectedNutrientId && (
                        <td>
                          {(() => {
                            const nutrientPer100g = food.nutrients[selectedNutrientId] || 0
                            const selectedNutrient = nutrients.find(n => n.id === selectedNutrientId)
                            const unit = selectedNutrient?.unit || ''

                            if (sortBy === 'nutrientPer100g') {
                              return (
                                <span>
                                  <strong>{nutrientPer100g.toFixed(1)}</strong>
                                  <small className="text-muted ms-1">{t('suggestions.unit.per100g', { unit })}</small>
                                </span>
                              )
                            } else {
                              const nutrientPerPortion = nutrientPer100g * (food.portionGrams / 100)
                              return (
                                <span>
                                  <strong>{nutrientPerPortion.toFixed(1)}</strong>
                                  <small className="text-muted ms-1">{t('suggestions.unit.perPortion', { unit })}</small>
                                </span>
                              )
                            }
                          })()}
                        </td>
                      )}

                      <td className="text-center">
                        {option.existingGrams > 0 ? (
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span className="badge bg-secondary text-white" style={{ fontSize: '10px' }}>
                              {(option.existingGrams / food.portionGrams).toFixed(1)}
                            </span>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeFood(food.id)}
                              title={t('suggestions.tooltip.removeFood', { name: localizedName })}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addFood(food.id)}
                            title={t('suggestions.tooltip.addFood', { grams: food.portionGrams, name: localizedName })}
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
